import { Component, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { forkJoin } from 'rxjs';

import { BaseComponent, BooleanStrings, ReportTemplate, ReportToolbarComponent } from '../../../../../tms-shared';
import { OrderStatuses, OrderTypes, ReportNames, ReportTypes } from '../../../../constants';
import { DocumentService, SalesOrderService } from '../../../../services';

@Component({
  selector: 'app-sales-order-reports',
  templateUrl: './sales-order-reports.component.html',
  styleUrl: './sales-order-reports.component.scss',
})
export class SalesOrderReportsComponent extends BaseComponent implements OnInit {
  @ViewChild(ReportToolbarComponent) reportToolbar: ReportToolbarComponent;
  @ViewChild('salesOrderConfirmation') salesOrderConfirmationReport: TemplateRef<any>;
  @ViewChild('salesOrderBalances') salesOrderBalances: TemplateRef<any>;
  @ViewChild('salesBackOrder') salesBackOrder: TemplateRef<any>;
  @ViewChild('salesQuotation') salesQuotationReport: TemplateRef<any>;
  @ViewChild('proformaReport') proformaReportReport: TemplateRef<any>;
  @ViewChild('depositReport') depositReport: TemplateRef<any>;
  @ViewChild('pickingList') pickingListReport: TemplateRef<any>;

  @Input() salesOrderHeaderId: string;

  constructor(
    private salesOrderService: SalesOrderService,
    private documentService: DocumentService,
    private route: ActivatedRoute,
  ) {
    super();
  }

  ngOnInit() {
    if (!this.salesOrderHeaderId) {
      this.salesOrderHeaderId = this.route.snapshot.paramMap.get('id');
    }
    this.getReports();

    this.salesOrderService.refreshDetails.pipe(takeUntil(this.destroy$)).subscribe(() => this.getReports());
  }

  private getReports() {
    forkJoin([
      this.salesOrderService.getSalesOrderHeader(this.salesOrderHeaderId),
      this.salesOrderService.getSalesOrderEmailContacts(this.salesOrderHeaderId),
      this.documentService.getDocument(ReportNames.salesOrderConfirmation),
      this.documentService.getDocument(ReportNames.salesQuotation),
      this.documentService.getDocument(ReportNames.salesOrderBalances),
      this.documentService.getDocument(ReportNames.salesBackOrder),
      this.documentService.getDocument(ReportNames.proformaInvoice),
      this.documentService.getDocument(ReportNames.depositInvoice),
      this.documentService.getDocument(ReportNames.pickingList),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        ([
          salesOrderHeader,
          contacts,
          confirmationDocument,
          quotationDocument,
          balancesDocument,
          backOrderDocument,
          proformaDocument,
          depositDocument,
          pickingDocument,
        ]) => {
          const reports: ReportTemplate[] = [];
          if (salesOrderHeader.orderType === OrderTypes.sales || salesOrderHeader.orderType === OrderTypes.samples) {
            reports.push({
              report: $localize`Sales Order Confirmation`,
              template: this.salesOrderConfirmationReport,
              email: contacts.salesOrderContactEmailAddress ?? contacts.customerConfirmationEmailAddress,
              suffix: salesOrderHeader.orderNo.toString(),
              sellingCompany: salesOrderHeader.sellingCompany,
              emailBody: confirmationDocument?.documentMessage,
              emailContacts: contacts.customerContacts,
              type: ReportTypes.salesOrderConfirmation,
            });
          }
          if (salesOrderHeader.orderType === OrderTypes.quotation) {
            reports.push({
              report: $localize`Sales Quotation`,
              template: this.salesQuotationReport,
              email: contacts.salesOrderContactEmailAddress ?? contacts.customerConfirmationEmailAddress,
              suffix: salesOrderHeader.orderNo.toString(),
              sellingCompany: salesOrderHeader.sellingCompany,
              emailBody: quotationDocument?.documentMessage,
              emailContacts: contacts.customerContacts,
              type: ReportTypes.quotation,
            });
          }
          if (
            salesOrderHeader.orderStatus !== OrderStatuses.complete &&
            salesOrderHeader.orderStatus !== OrderStatuses.cancelled
          ) {
            reports.push({
              report: $localize`Sales Order Balances`,
              template: this.salesOrderBalances,
              email: contacts.salesOrderContactEmailAddress ?? contacts.customerConfirmationEmailAddress,
              suffix: salesOrderHeader.orderNo.toString(),
              sellingCompany: salesOrderHeader.sellingCompany,
              emailBody: balancesDocument?.documentMessage,
              emailContacts: contacts.customerContacts,
              type: ReportTypes.salesOrderBalances,
            });
            reports.push({
              report: $localize`Sales Back Order`,
              template: this.salesBackOrder,
              email: contacts.salesOrderContactEmailAddress ?? contacts.customerConfirmationEmailAddress,
              suffix: salesOrderHeader.orderNo.toString(),
              sellingCompany: salesOrderHeader.sellingCompany,
              emailBody: backOrderDocument?.documentMessage,
              emailContacts: contacts.customerContacts,
              type: ReportTypes.salesOrderBackOrder,
            });
          }
          if (salesOrderHeader.proformaOrder === BooleanStrings.yes) {
            reports.push({
              report: $localize`Proforma Invoice`,
              template: this.proformaReportReport,
              email: contacts.customerInvoiceEmailAddress,
              suffix: salesOrderHeader.orderNo.toString(),
              sellingCompany: salesOrderHeader.sellingCompany,
              emailBody: proformaDocument?.documentMessage,
              emailContacts: contacts.customerContacts,
              type: ReportTypes.proforma,
            });
          }
          if (salesOrderHeader.salesOrderDeposits) {
            reports.push({
              report: $localize`Deposit Invoice`,
              template: this.depositReport,
              email: contacts.customerInvoiceEmailAddress,
              suffix: salesOrderHeader.orderNo.toString(),
              sellingCompany: salesOrderHeader.sellingCompany,
              emailBody: depositDocument?.documentMessage,
              emailContacts: contacts.customerContacts,
              type: ReportTypes.depositInvoice,
            });
          }
          reports.push({
            report: $localize`Picking List`,
            template: this.pickingListReport,
            suffix: salesOrderHeader.orderNo.toString(),
            sellingCompany: salesOrderHeader.sellingCompany,
            emailBody: pickingDocument?.documentMessage,
            emailContacts: contacts.customerContacts,
            type: ReportTypes.pickingList,
          });

          this.reportToolbar.setReports(reports);
        },
      );
  }
}
